"use client";
import { useState, useEffect, useRef, useSyncExternalStore } from "react";
import { useRouter } from "next/navigation";
import Link from "next/link";
import { ArrowUpRight, ChevronDown, RefreshCcw, CircleDot, Mail, Phone, Loader2 } from "lucide-react";
import GlobalTitle from "@shared/GlobalTitle";
import { validateContactForm, isSpamContent } from "@/utils/validation";
import { siteConfig } from "@/lib/siteConfig";
import { logFormError } from "@/utils/formErrorLogger";
import { useLivePaths } from "@/context/LiveNavContext";
import { resolveLiveHref } from "@/lib/live-nav";
import MailtoLink from "@shared/MailtoLink";

function inputCls(isDark, hasError = false, extra = "") {
  return [
    "w-full rounded-[10px] border px-3.5 py-3 text-sm outline-none",
    "transition-[border-color,box-shadow]",
    "focus:border-primary focus:shadow-[0_0_0_3px_rgba(224,39,74,0.15)]",
    isDark
      ? `bg-white/5 text-white placeholder:text-white/40 ${hasError ? "border-primary/60" : "border-white/[0.12]"}`
      : `bg-white/90 text-ink placeholder:text-[#8A92A4] ${hasError ? "border-primary/60" : "border-black/10"}`,
    extra,
  ].filter(Boolean).join(" ");
}

function ServiceLinks({ services, isDark, highlightLine }) {
  const divider = isDark ? "border-white/[0.08]" : "border-black/[0.08]";
  const cols = highlightLine === "single" ? "grid-cols-1" : "grid-cols-1 sm:grid-cols-2";
  const top = highlightLine === "single" ? "mt-4" : "mt-8";
  const livePaths = useLivePaths();
  return (
    <div className={`${top} grid ${cols} gap-x-6 border-t ${divider}`}>
      {services.map((s, i) => {
        const href = s.href ? resolveLiveHref(s.href, livePaths) : null;
        const Row = href ? Link : "div";
        return (
          <Row
            key={i}
            {...(href && { href })}
            className={`grid grid-cols-[auto_1fr_auto] gap-x-3 items-start py-3.5 px-1 border-b ${divider} ${isDark ? "text-white" : "text-ink"} ${href ? "transition-all duration-200 hover:translate-x-0.5 hover:text-primary" : ""}`}
          >
            <span className="font-mono text-[12px] tracking-[0.14em] text-primary">
              <CircleDot size={15} />
            </span>
            {s.points?.length > 0 ? (
              <span>
                <span className="block text-sm font-medium leading">{s.label}</span>
                <ul className={`mt-1.5 space-y-1 text-[13px] leading-snug ${isDark ? "text-white/65" : "text-[#5B6478]"}`}>
                  {s.points.map((p, j) => <li key={j}>{p}</li>)}
                </ul>
              </span>
            ) : (
              <span className="text-sm font-medium leading">{s.label}</span>
            )}
            {href && (
              <ArrowUpRight size={14} className="text-primary" />
            )}
          </Row>
        );
      })}
    </div>
  );
}

/* ── Contact form ── */
function ContactForm({ isDark, serviceOptions, defaultService = "" }) {
  const router = useRouter();
  const [form, setForm] = useState({
    name: "",
    phone: "",
    email: "",
    message: "",
    captcha: "",
    service: defaultService,
  });

  const [code, setCode] = useState("");
  const [captchaToken, setCaptchaToken] = useState("");

  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const [errors, setErrors] = useState({});

  const lastSubmitTime = useRef(0);
  const hasInteracted = useRef(false);
  const honeypotBlocked = useRef(false);

  const fetchCaptcha = () =>
    fetch("/api/captcha?purpose=hire")
      .then((res) => res.json())
      .then((data) => {
        setCode(data.display ?? "");
        setCaptchaToken(data.token ?? "");
        setForm((prev) => ({ ...prev, captcha: "" }));
      })
      .catch(() => {
        // silent — user can hit refresh button
      });

  useEffect(() => { fetchCaptcha(); }, []);

  /* Strip HTML tags and null bytes before sending to API */
  const sanitize = (str) => (str ?? "").replace(/<[^>]*>/g, "").replace(/\0/g, "").trim();

  const set = (key) => (e) => {
    const value = e.target.value;

    setForm((prev) => ({
      ...prev,
      [key]: value,
    }));

    /* Remove field error while typing */
    setErrors((prev) => ({
      ...prev,
      [key]: "",
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    /* Interaction check — bots don't focus or type in fields */
    if (!hasInteracted.current) {
      setErrors({ submit: "Please fill in the form before submitting." });
      logFormError({ form: "hire", errorType: "bot_check", message: "Form submitted without user interaction" });
      return;
    }

    /* Session submission limit */
    const SESSION_KEY = "cform_count";
    const MAX_SUBMISSIONS = parseInt(process.env.NEXT_PUBLIC_MAX_FORM_SUBMISSIONS ?? "10", 10);
    const submitCount = parseInt(sessionStorage.getItem(SESSION_KEY) ?? "0", 10);
    if (submitCount >= MAX_SUBMISSIONS) {
      setErrors({ submit: "Too many submissions. Please contact us directly via email or phone." });
      logFormError({ form: "hire", errorType: "session_limit", message: `Session submission limit reached (count: ${submitCount})`, email: form.email, service: form.service });
      return;
    }

    /* Submission cooldown — prevent rapid resubmits */
    const COOLDOWN_MS = parseInt(process.env.NEXT_PUBLIC_FORM_COOLDOWN_MS ?? "5000", 10);
    if (lastSubmitTime.current && Date.now() - lastSubmitTime.current < COOLDOWN_MS) {
      setErrors({ submit: "Please wait a few seconds before trying again." });
      logFormError({ form: "hire", errorType: "cooldown", message: "Rapid resubmit attempt blocked" });
      return;
    }

    /* Honeypot — capture DOM value once; reused in both check and server payload */
    const honeypotValue = new FormData(e.target).get('optional-field') || "";
    if (honeypotValue || honeypotBlocked.current) {
      honeypotBlocked.current = true;
      setErrors({ submit: "Something went wrong. Please refresh the page and try again." });
      logFormError({ form: "hire", errorType: "honeypot", message: "Honeypot field filled — bot or manual manipulation detected", email: form.email, service: form.service });
      return;
    }

    /* Spam content check */
    if (isSpamContent(form.message) || isSpamContent(form.name)) {
      setErrors({ submit: "Your message was flagged as spam. Please remove any links or suspicious content." });
      logFormError({ form: "hire", errorType: "spam", message: "Spam content detected in form fields", email: form.email, service: form.service });
      return;
    }

    const validationErrors = validateContactForm({ form, code });

    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);

      if (validationErrors.captcha) {
        fetchCaptcha();
      }

      return;
    }

    lastSubmitTime.current = Date.now();
    sessionStorage.setItem(SESSION_KEY, String(submitCount + 1));

    try {
      setErrors({});
      setLoading(true);

      const res = await fetch("/api/contact", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          service: sanitize(form.service),
          name: sanitize(form.name),
          phone: sanitize(form.phone),
          email: sanitize(form.email),
          message: sanitize(form.message),
          page: typeof window !== "undefined" ? window.location.pathname : null,
          captchaToken,
          captchaInput: form.captcha,
          captchaPurpose: "hire",
          "optional-field": honeypotValue,
        }),
      });

      const data = await res.json();
      if (!res.ok) throw new Error(data?.error || "Server error");

      setSubmitted(true);
      router.push("/thank-you");
    } catch (err) {
      // console.error(err);
      setErrors({ submit: "Something went wrong. Please try again." });
      logFormError({ form: "hire", errorType: "submission_failed", message: err?.message ?? "Unknown error", email: form.email, service: form.service });
    } finally {
      setLoading(false);
    }
  };



  const errorCls = "mt-1 text-xs text-primary";

  /* Card classes — backdrop-blur + shadow all in Tailwind */
  const cardCls = isDark
    ? "bg-white/4 backdrop-blur-[28px] backdrop-saturate-[150%] border border-white/10 shadow-[0_30px_80px_-20px_rgba(0,0,0,0.5),inset_0_1px_0_rgba(255,255,255,0.08)]"
    : "bg-white/65 backdrop-blur-2xl backdrop-saturate-[160%] border border-white/80 shadow-[0_30px_70px_-24px_rgba(20,10,30,0.16),inset_0_1px_0_rgba(255,255,255,0.95)]";

  return (
    <div className="relative">
      <div className={`relative rounded-[28px] p-8 z-8 ${cardCls}`}>
        {submitted ? (
          <div className="flex flex-col items-center justify-center py-10 text-center gap-4">
            <div className={`w-16 h-16 rounded-full flex items-center justify-center ${isDark ? "bg-primary/20" : "bg-primary/10"}`}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="w-8 h-8 text-primary"><polyline points="20 6 9 17 4 12" /></svg>
            </div>
            <h3 className={`font-serif text-2xl font-medium m-0 ${isDark ? "text-white" : "text-ink"}`}>
              Message <span className="text-primary italic">Received!</span>
            </h3>
            <p className={`text-sm max-w-sm ${isDark ? "text-white/60" : "text-[#5B6478]"}`}>
              Thank you for reaching out. We&apos;ll get back to you shortly.
            </p>
            <button
              type="button"
              onClick={() => {
                setSubmitted(false);
                setForm({ name: "", phone: "", email: "", message: "", service: defaultService, captcha: "" });
                fetchCaptcha();
              }}
              className={`text-sm underline underline-offset-2 cursor-pointer ${isDark ? "text-white/50 hover:text-white" : "text-[#8A92A4] hover:text-ink"}`}
            >
              Send another message
            </button>
          </div>
        ) : (
          <>
            <div className="flex items-center justify-between mb-6">
              <h3 className={`font-serif text-3xl font-medium tracking-[-0.015em] m-0 ${isDark ? "text-white" : "text-ink"}`}>
                Let&apos;s Discuss <em className="italic text-primary">Your Needs!</em>
              </h3>
            </div>
            <form
              onSubmit={handleSubmit}
              onFocus={() => { hasInteracted.current = true; }}
              className="flex flex-col gap-4"
            >
              {/* Row 1 */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {/* Name */}
                <div>
                  <input
                    type="text"
                    placeholder="Name"
                    aria-label="Name"
                    value={form.name}
                    onChange={set("name")}
                    className={inputCls(isDark, !!errors.name)}
                  />

                  {errors.name && (
                    <p className={errorCls}>
                      {errors.name}
                    </p>
                  )}
                </div>

                {/* Phone */}
                <div>
                  <input
                    type="tel"
                    placeholder="Phone Number"
                    aria-label="Phone Number"
                    value={form.phone}
                    onChange={set("phone")}
                    className={inputCls(isDark, !!errors.phone)}
                  />

                  {errors.phone && <p className={errorCls}>{errors.phone}</p>}
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {/* Email */}
                <div>
                  <input
                    type="text"
                    inputMode="email"
                    autoComplete="email"
                    placeholder="Email"
                    aria-label="Email"
                    value={form.email}
                    onChange={set("email")}
                    className={inputCls(isDark, !!errors.email)}
                  />

                  {errors.email && (
                    <p className={errorCls}>
                      {errors.email}
                    </p>
                  )}
                </div>

                {/* Service */}
                <div>
                  <div className="relative">
                    <select
                      value={form.service}
                      onChange={set("service")}
                      aria-label="Service"
                      style={{
                        WebkitAppearance: "none",
                        MozAppearance: "none",
                        appearance: "none",
                        backgroundImage: "none",
                      }}
                      className={`${inputCls(isDark, false, "pr-9 cursor-pointer")} ${!form.service ? "text-secondary" : "text-foreground"} max-h-80 overflow-y-auto`}
                    >
                      <option value="" disabled>
                        Select Service
                      </option>

                      {serviceOptions.map((s, i) => (
                        <option
                          key={i}
                          value={s}
                          className="text-ink bg-white"
                        >
                          {s}
                        </option>
                      ))}
                    </select>

                    <ChevronDown
                      size={12}
                      className={`absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none ${isDark
                        ? "text-white/50"
                        : "text-[#8A92A4]"
                        }`}
                    />
                  </div>

                </div>
              </div>

              {/* Message */}
              <div>
                <textarea
                  placeholder="Describe your needs"
                  aria-label="Message"
                  rows={3}
                  value={form.message}
                  onChange={set("message")}
                  className={inputCls(isDark, !!errors.message, "resize-y min-h-20")}
                />

                {errors.message && (
                  <p className={`mt-0 text-xs text-primary`}>
                    {errors.message}
                  </p>
                )}
              </div>

              {/* Captcha */}
              <div className="captach-group">
                <div className="grid grid-cols-[1fr_auto_auto] gap-2.5 items-end">
                  <div>
                    <input
                      type="text"
                      placeholder="Enter the text displayed"
                      aria-label="Captcha code"
                      value={form.captcha}
                      onChange={set("captcha")}
                      maxLength={6}
                      className={inputCls(isDark, !!errors.captcha)}
                    />
                  </div>

                  {/* Captcha Code */}
                  <div
                    suppressHydrationWarning
                    className={`w-22 h-11.5 flex items-center justify-center rounded-[10px] border font-mono text-lg italic tracking-[0.2em] select-none line-through decoration-primary ${isDark
                      ? "bg-white/8 border-white/12 text-white"
                      : "bg-[#F5F5F7] border-black/10 text-ink"
                      }`}
                  >
                    {code}
                  </div>

                  {/* Refresh */}
                  <button
                    type="button"
                    title="Change Captcha"
                    onClick={() => {
                      fetchCaptcha();
                      setErrors((prev) => ({
                        ...prev,
                        captcha: "",
                      }));
                    }}
                    aria-label="Change Captcha"
                    className={`w-11.5 h-11.5 flex items-center justify-center cursor-pointer rounded-[10px] border transition-colors ${isDark
                      ? "bg-white/5 border-white/12 hover:bg-white/10"
                      : "bg-white border-black/10 hover:bg-gray-50"
                      }`}
                  >
                    <RefreshCcw
                      size={16}
                      className={
                        isDark
                          ? "text-white/60"
                          : "text-ink"
                      }
                    />
                  </button>
                </div>

                {errors.captcha && (
                  <p className="mt-0 text-xs text-primary relative top-2">
                    {errors.captcha}
                  </p>
                )}
              </div>

              {/* Honeypot Field */}
              <div
                className="absolute opacity-0 pointer-events-none -z-10"
                aria-hidden="true"
              >
                <input
                  type="text"
                  name="optional-field"
                  tabIndex="-1"
                  autoComplete="off"
                  defaultValue=""
                />
              </div>

              {/* Privacy */}
              <p
                className={`text-[11px] leading-relaxed ${isDark
                  ? "text-white/55"
                  : "text-[#8A92A4]"
                  }`}
              >
                By submitting, you consent to the use
                of your personal data in accordance
                with Velan&apos;s{" "}
                <Link
                  href="/privacy-policy"
                  className="text-primary underline"
                >
                  privacy policy
                </Link>
              </p>

              {/* Submit Error */}
              {errors.submit && (
                <p className="text-sm text-primary">
                  {errors.submit}
                </p>
              )}

              {/* Submit Button */}
              <div className="flex justify-center mt-2">
                <button
                  type="submit"
                  disabled={loading}
                  className="bg-red-gradient border-0 rounded-md px-8 py-3.5 cursor-pointer font-semibold text-[13px] tracking-[0.16em] uppercase disabled:opacity-60 btn-glass group"
                >
                  <span className="relative z-10 flex items-center gap-2 text-white group-hover:text-secondary transition-colors duration-400">
                    {loading ? (
                      <><Loader2 size={15} className="animate-spin" /> Sending…</>
                    ) : (
                      <>
                        Submit
                        <ArrowUpRight size={16} className="transition-transform duration-200 group-hover:translate-x-1 shrink-0" />
                      </>
                    )}
                  </span>
                </button>
              </div>
            </form>
          </>
        )}
      </div>

      <div className="absolute -bottom-1.5 left-7 right-7 h-1.5 rounded-b-xl bg-primary" />
    </div>
  );
}

/* ── Contact info bar — revealed client-side only to prevent bot scraping & CGI email obfuscation ── */
const noopSubscribe = () => () => {};

function ContactInfoBar({ isDark }) {
  const isClient = useSyncExternalStore(noopSubscribe, () => true, () => false);
  const info = isClient
    ? { email: siteConfig.email, phone: siteConfig.phone }
    : { email: "", phone: "" };

  const glassCls = isDark
    ? "bg-white/[0.06] backdrop-blur-2xl border border-white/[0.12] shadow-[0_8px_40px_rgba(0,0,0,0.28),inset_0_1px_0_rgba(255,255,255,0.08)]"
    : "bg-white/55 backdrop-blur-xl border border-white/75 shadow-[0_8px_32px_rgba(20,10,30,0.10),inset_0_1px_0_rgba(255,255,255,0.95)]";

  const itemCls = isDark
    ? "text-white/80 hover:text-white"
    : "text-[#5B6478] hover:text-ink";

  const iconCls = isDark ? "text-primary" : "text-primary";

  if (!info.email && !info.phone) return null;

  return (
    <>
      <h4 className={`font-serif text-2xl font-medium tracking-[-0.015em] text-center mt-5 ${isDark ? "text-white" : "text-ink"}`}>
        Make a <span className="text-primary">Quick Connect</span>
      </h4>
      <div
        className={`mt-4 rounded-2xl px-6 py-4 flex flex-wrap items-center justify-start md:justify-center gap-x-8 gap-y-3 relative overflow-hidden ${glassCls}`}
        aria-label="Contact details"
      >
        <div className="absolute -bottom-2 -right-12 w-20 h-20 bg-secondary/20 rounded-full pointer-events-none" />
        <div className="absolute -bottom-2 -left-12 w-20 h-20 bg-secondary/20 rounded-full pointer-events-none" />
        {info.email && (
          <MailtoLink
            email={info.email}
            className={`flex items-center gap-2.5 text-md font-medium transition-colors duration-200 no-underline ${itemCls}`}
          >
            <span className={`flex items-center justify-center w-8 h-8 rounded-full ${isDark ? "bg-white/10" : "bg-primary/10"}`}>
              <Mail size={14} className={iconCls} />
            </span>
            <span suppressHydrationWarning>{info.email}</span>
          </MailtoLink>
        )}

        {info.email && info.phone && (
          <span className={`hidden sm:block w-px h-5 ${isDark ? "bg-white/15" : "bg-black/10"}`} aria-hidden="true" />
        )}

        {info.phone && (
          <a
            href={`tel:${info.phone.replace(/\s/g, "")}`}
            className={`flex items-center gap-2.5 text-md font-medium transition-colors duration-200 no-underline ${itemCls}`}
          >
            <span className={`flex items-center justify-center w-8 h-8 rounded-full ${isDark ? "bg-white/10" : "bg-primary/10"}`}>
              <Phone size={14} className={iconCls} />
            </span>
            <span suppressHydrationWarning>{info.phone}</span>
          </a>
        )}
      </div>
    </>
  );
}

/**
 * HireSection — two-column: left content + service links, right contact form.
 *
 * Props
 *   data.eyebrow        string
 *   data.titleText      string
 *   data.coloredText    string
 *   data.descriptions   string[]
 *   data.services       { label, href, points? }[] — points: optional string[] shown under the label
 *   data.serviceOptions string[]
 *   data.defaultService string — pre-selects this page's matching option; still editable
 *   theme               "dark" | "light"
 */
export default function HireSection({ data, theme = "light" }) {
  const isDark = theme === "dark";

  return (
    <section className={`relative overflow-hidden isolate ${isDark ? "bg-ink" : "bg-white"}`}>

      {/* ── Gradient orbs ── */}
      {isDark ? (
        <>
          <div className="absolute rounded-full pointer-events-none bottom-[-25%] left-[-10%] w-160 h-160 bg-[radial-gradient(circle,rgba(74,124,199,0.18)_0%,transparent_70%)] blur-xl" />
        </>
      ) : (
        <>
          <div className="absolute rounded-full pointer-events-none top-[-10%] right-[-5%] w-150 h-150 bg-[radial-gradient(circle,rgba(255,194,209,0.4)_0%,transparent_65%)] blur-2xl" />
          <div className="absolute rounded-full pointer-events-none bottom-[-15%] left-[-8%] w-145 h-145 bg-[radial-gradient(circle,rgba(74,124,199,0.15)_0%,transparent_70%)] blur-2xl" />
        </>
      )}

      {/* ── Grid overlay with radial mask (dark only) ── */}
      {isDark && (
        <div className="absolute inset-0 pointer-events-none select-none">
          <div
            className="absolute inset-0 opacity-[0.035]"
            style={{
              backgroundImage:
                'linear-gradient(rgba(255, 255, 255, 0.33) 1px, transparent 1px), linear-gradient(90deg, rgba(255, 255, 255, 0.29) 1px, transparent 1px)',
              backgroundSize: '64px 64px',
            }}
          />
          <div className="absolute -top-40 left-1/2 -translate-x-1/2 w-[900px] h-[400px] bg-primary/[0.08] rounded-full blur-[130px]" />
          <div className="absolute bottom-0 right-0 w-[400px] h-[300px] bg-primary/[0.05] rounded-full blur-[90px]" />
        </div>
      )}

      {/* ── Hex outlines (light only) ── */}
      {!isDark && (
        <svg className="absolute inset-0 w-full h-full pointer-events-none opacity-[0.06]" viewBox="0 0 1440 900" preserveAspectRatio="xMidYMid slice" aria-hidden="true">
          <g stroke="#E0274A" strokeWidth="1" fill="none">
            <path d="M120 200 L160 220 L160 260 L120 280 L80 260 L80 220 Z" />
            <path d="M1280 600 L1320 620 L1320 660 L1280 680 L1240 660 L1240 620 Z" />
            <path d="M1280 100 L1320 120 L1320 160 L1280 180 L1240 160 L1240 120 Z" />
            <path d="M120 700 L160 720 L160 760 L120 780 L80 760 L80 720 Z" />
          </g>
        </svg>
      )}

      {/* ── Content ── */}
      <div className="relative z-[2] max-w-[1440px] mx-auto px-6 py-12 lg:py-20">
        <div className="grid grid-cols-1 items-start gap-14 lg:gap-18 lg:grid-cols-[1.05fr_0.95fr]">

          {/* Left */}
          <div>
            <GlobalTitle
              topText={data.eyebrow}
              titleText={data.titleText}
              coloredText={data.coloredText}
              classes={`text-left${isDark ? " text-white" : ""}`}
            />

            <div className={`mt-4 flex flex-col gap-3.5 max-w-150 text-[15.5px] leading-[1.65] ${isDark ? "text-white/80" : "text-[#5B6478]"}`}>
              {data.descriptions?.map((p, i) => (
                <p key={i} className="m-0">{p}</p>
              ))}
            </div>

            {data.services?.length > 0 && (
              <ServiceLinks services={data.services} isDark={isDark} highlightLine={data.highlightLine} />
            )}
          </div>

          {/* Right */}
          <div>
            <ContactForm isDark={isDark} serviceOptions={data.serviceOptions ?? []} defaultService={data.defaultService ?? ""} />
            <ContactInfoBar isDark={isDark} />
          </div>

        </div>
      </div>
    </section>
  );
}